import React, { useMemo, useState, useCallback } from 'react';
import { Calendar, dateFnsLocalizer, View } from 'react-big-calendar';
import withDragAndDrop, {
  EventInteractionArgs,
} from 'react-big-calendar/lib/addons/dragAndDrop';
import {
  format,
  parse,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfDay,
  endOfDay,
  getDay,
  add,
} from 'date-fns';
import { it } from 'date-fns/locale';
import { Task } from '@tassomutuo-backoffice/types';
import { Box, Paper, Chip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ScheduleIcon from '@mui/icons-material/Schedule';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import './CalendarView.css';
import { GoogleCalendarEvent } from '../../../services/api/google-calendar/google-calendar';

type CalendarEventResource = Task | GoogleCalendarEvent;

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: CalendarEventResource;
  isGoogleEvent?: boolean;
}

const DragAndDropCalendar = withDragAndDrop<CalendarEvent>(Calendar);

const locales = {
  'it-IT': it,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { locale: it }),
  getDay,
  locales,
});

interface CalendarViewProps {
  tasks: Task[];
  googleEvents?: GoogleCalendarEvent[];
  onTaskDateChange: (taskId: string, newDate: Date) => void;
  onSelectEvent?: (task: Task) => void;
  onSelectGoogleEvent?: (event: GoogleCalendarEvent) => void;
  onSelectSlot?: (slotInfo: { start: Date; end: Date }) => void;
  onRangeChange?: (range: { start: Date; end: Date }) => void;
}

// Custom Event Component
const CustomEvent = ({ event }: { event: CalendarEvent }) => {
  const isGoogleEvent = event.isGoogleEvent || false;

  if (isGoogleEvent) {
    const googleEvent = event.resource as GoogleCalendarEvent;
    return (
      <Box
        className="google-event-container"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          p: 0.5,
          position: 'relative',
          overflow: 'hidden',
          gap: 0.5,
          maxWidth: '200px',
        }}
      >
        <Box
          sx={{
            width: '90%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minHeight: 0,
          }}
        >
          {/* Event Title - scales with container height */}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: 'clamp(0.6rem, 2vh, 0.95rem)',
              lineHeight: 1.2,
              color: 'white',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              maxWidth: '100%',
            }}
          >
            {googleEvent.summary}
          </Typography>

          {/* Location (if present) - only show if there's enough space */}
          {googleEvent.location && (
            <Typography
              variant="body2"
              className="google-event-location"
              sx={{
                fontSize: 'clamp(0.5rem, 1.5vh, 0.75rem)',
                lineHeight: 1.2,
                color: 'rgba(0, 0, 0, 0.6)',
                mt: 0.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
              }}
            >
              {googleEvent.location}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  // Task event
  const task = event.resource as Task;
  const isCompleted = task.completed || false;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        p: 1.5,
        position: 'relative',
        overflow: 'hidden',
        gap: 1,
        maxWidth: '200px',
      }}
    >
      <Box
        sx={{
          width: '5px',
          minWidth: '5px',
          height: '100%',
          backgroundColor: isCompleted ? '#059573' : '#F9A602',
          borderRadius: '5px',
          flexShrink: 0,
        }}
      />
      <Box sx={{ width: '90%' }}>
        {/* Lead Name */}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: '0.95rem',
            color: '#000',
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {task.lead.properties.dealname}
        </Typography>

        {/* Action/Description */}
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.85rem',
            color: 'rgba(0, 0, 0, 0.6)',
            mb: 1.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {task.action}
        </Typography>

        {/* Status Chip */}

        <Chip
          icon={
            isCompleted ? (
              <CheckIcon sx={{ fontSize: '1rem' }} />
            ) : (
              <ScheduleIcon sx={{ fontSize: '1rem' }} />
            )
          }
          label={isCompleted ? 'Fatto' : 'Da fare'}
          size="small"
          sx={{
            borderRadius: '5px',
            backgroundColor: isCompleted ? '#DDF8F2' : '#FFEECC',
            color: isCompleted ? '#059573' : '#F9A602',
            fontWeight: 500,
            fontSize: '0.75rem',
            height: '20px',
            alignSelf: 'flex-start',
            '& .MuiChip-icon': {
              color: isCompleted ? '#059573' : '#F9A602',
            },
          }}
        />
      </Box>
    </Box>
  );
};

const CalendarView: React.FC<CalendarViewProps> = ({
  tasks,
  googleEvents = [],
  onTaskDateChange,
  onSelectEvent,
  onSelectGoogleEvent,
  onSelectSlot,
  onRangeChange,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState<View>('week');

  // Calculate date range based on view and current date
  const calculateRange = useCallback((date: Date, view: View) => {
    let start: Date;
    let end: Date;

    if (view === 'week') {
      start = startOfWeek(date, { locale: it });
      end = endOfWeek(date, { locale: it });
    } else if (view === 'day') {
      start = startOfDay(date);
      end = endOfDay(date);
    } else {
      // month or agenda
      start = startOfMonth(date);
      end = endOfMonth(date);
    }

    return { start, end };
  }, []);

  // Notify parent when range changes
  const handleRangeChange = useCallback(
    (
      range:
        | Date[]
        | { start: Date | string; end: Date | string }
        | { start: Date; end: Date },
      view?: View
    ) => {
      let start: Date;
      let end: Date;

      if (Array.isArray(range)) {
        // Week or day view returns an array of dates
        start = range[0];
        end = range[range.length - 1];
      } else {
        // Month view returns an object with start and end
        start =
          typeof range.start === 'string' ? new Date(range.start) : range.start;
        end = typeof range.end === 'string' ? new Date(range.end) : range.end;
      }

      // Update internal state
      setCurrentDate(start);

      // Notify parent
      onRangeChange?.({ start, end });
    },
    [currentView, onRangeChange]
  );

  // Notify parent of initial range
  React.useEffect(() => {
    const initialRange = calculateRange(currentDate, currentView);
    onRangeChange?.(initialRange);
  }, []); // Only run on mount

  const handleViewChange = useCallback(
    (view: View) => {
      setCurrentView(view);
      const newRange = calculateRange(currentDate, view);
      onRangeChange?.(newRange);
    },
    [currentDate, calculateRange, onRangeChange]
  );

  const events = useMemo<CalendarEvent[]>(() => {
    const taskEvents = tasks.map((task) => {
      const taskDate = new Date(task.date);
      return {
        id: task.id,
        title: `${task.lead.properties.dealname} - ${task.action}`,
        start: taskDate,
        end: add(taskDate, { minutes: 15 }),
        resource: task,
        isGoogleEvent: false,
      };
    });

    const googleCalendarEvents = googleEvents.map((event) => {
      return {
        id: event.id,
        title: event.summary,
        start: new Date(event.start.dateTime),
        end: new Date(event.end.dateTime),
        resource: event,
        isGoogleEvent: true,
      };
    });

    return [...taskEvents, ...googleCalendarEvents];
  }, [tasks, googleEvents]);

  const handleEventDrop = (args: EventInteractionArgs<CalendarEvent>) => {
    // Prevent dropping Google Calendar events
    if (args.event.isGoogleEvent) {
      return;
    }

    const newDate =
      typeof args.start === 'string' ? new Date(args.start) : args.start;
    onTaskDateChange(args.event.id, newDate);
  };

  /* const handleEventResize = (args: EventInteractionArgs<CalendarEvent>) => {
    const newDate =
      typeof args.start === 'string' ? new Date(args.start) : args.start;
    onTaskDateChange(args.event.id, newDate);
  };
*/
  const eventStyleGetter = (event: CalendarEvent) => {
    if (event.isGoogleEvent) {
      // Google Calendar event style
      const style = {
        backgroundColor: '#039BE5',
        borderRadius: '8px',
        color: '#FFFFFF',
        border: 'none',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: 0,
      };
      return {
        style,
        className: 'google-calendar-event',
      };
    }

    // Task event style
    const task = event.resource as Task;
    const isCompleted = task.completed || false;
    const borderColor = isCompleted ? '#059573' : '#F9A602';

    const style = {
      backgroundColor: '#f5f5f5',
      borderRadius: '8px',
      borderLeft: `4px solid ${borderColor}`,
      color: '#000',
      border: 'none',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      padding: 0,
    };
    return {
      style,
      className: 'task-event',
    };
  };

  const handleSelectEvent = (event: CalendarEvent) => {
    if (event.isGoogleEvent) {
      onSelectGoogleEvent?.(event.resource as GoogleCalendarEvent);
    } else {
      onSelectEvent?.(event.resource as Task);
    }
  };

  const messages = {
    allDay: 'Tutto il giorno',
    previous: 'Precedente',
    next: 'Successivo',
    today: 'Oggi',
    month: 'Mese',
    week: 'Settimana',
    day: 'Giorno',
    agenda: 'Agenda',
    date: 'Data',
    time: 'Ora',
    event: 'Evento',
    noEventsInRange: 'Nessun task in questo periodo.',
    showMore: (total: number) => `+ Altri ${total}`,
  };

  return (
    <Paper elevation={0} sx={{ p: 3, height: 'calc(100vh - 200px)' }}>
      <Box sx={{ height: '100%' }}>
        <DragAndDropCalendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          culture="it-IT"
          messages={messages}
          onEventDrop={handleEventDrop}
          //onEventResize={handleEventResize}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={onSelectSlot}
          onRangeChange={handleRangeChange}
          onView={handleViewChange}
          selectable
          resizable={false}
          eventPropGetter={eventStyleGetter}
          components={{
            event: CustomEvent,
          }}
          defaultView="week"
          views={['week', 'day']}
          style={{ height: '100%' }}
        />
      </Box>
    </Paper>
  );
};

export default CalendarView;
